import { useState, useMemo } from 'react';
import { Invoice, InvoiceStatus } from '../../../mocks/invoices';
import { agencyInfo } from '../../../mocks/dashboard';

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  Paid: 'bg-emerald-100 text-emerald-700',
  Partial: 'bg-amber-100 text-amber-700',
  Unpaid: 'bg-rose-100 text-rose-700',
  Overdue: 'bg-red-100 text-red-700',
  Draft: 'bg-gray-100 text-gray-600',
  Cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_DOTS: Record<InvoiceStatus, string> = {
  Paid: 'bg-emerald-500',
  Partial: 'bg-amber-500',
  Unpaid: 'bg-rose-500',
  Overdue: 'bg-red-600',
  Draft: 'bg-gray-400',
  Cancelled: 'bg-gray-300',
};

interface InvoiceTableProps {
  invoices: Invoice[];
  onSelect: (invoice: Invoice) => void;
}

export default function InvoiceTable({ invoices, onSelect }: InvoiceTableProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'All'>('All');
  const brand = agencyInfo.brandColor;

  const statusTabs: (InvoiceStatus | 'All')[] = ['All', 'Paid', 'Partial', 'Unpaid', 'Overdue', 'Draft'];

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      const matchSearch =
        !search ||
        inv.number.toLowerCase().includes(search.toLowerCase()) ||
        inv.customer.toLowerCase().includes(search.toLowerCase()) ||
        inv.bookingId.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All' || inv.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [invoices, search, statusFilter]);

  const fmtAmount = (n: number) => `${agencyInfo.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const isOverdue = (inv: Invoice) => {
    const due = new Date(inv.dueDate);
    return due < new Date() && inv.status !== 'Paid' && inv.status !== 'Cancelled';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      {/* Search & Status Filters */}
      <div className="px-6 pt-5 pb-0">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search invoice #, customer, booking..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': `${brand}40` } as React.CSSProperties}
            />
          </div>
        </div>
        <div className="flex gap-1 border-b border-gray-100">
          {statusTabs.map((tab) => {
            const count = tab === 'All' ? invoices.length : invoices.filter((i) => i.status === tab).length;
            const active = statusFilter === tab;
            return (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1.5 ${
                  active ? 'border-b-2 text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
                style={active ? { borderColor: brand, color: brand } : {}}
              >
                {tab !== 'All' && (
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOTS[tab as InvoiceStatus]}`} />
                )}
                {tab}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-opacity-15' : 'bg-gray-100 text-gray-500'}`}
                  style={active ? { backgroundColor: `${brand}15`, color: brand } : {}}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-50">
              <th className="text-left px-6 py-3 font-medium">Invoice #</th>
              <th className="text-left px-4 py-3 font-medium">Customer</th>
              <th className="text-left px-4 py-3 font-medium">Booking</th>
              <th className="text-left px-4 py-3 font-medium">Issued</th>
              <th className="text-left px-4 py-3 font-medium">Due Date</th>
              <th className="text-right px-4 py-3 font-medium">Total</th>
              <th className="text-right px-4 py-3 font-medium">Balance</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Agent</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((inv) => (
              <tr
                key={inv.id}
                onClick={() => onSelect(inv)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${brand}12` }}>
                      <i className="ri-bill-line text-sm" style={{ color: brand }} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{inv.number}</p>
                      {isOverdue(inv) && inv.status === 'Overdue' && (
                        <span className="text-xs text-red-500 font-medium">OVERDUE</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                      style={{ backgroundColor: brand }}
                    >
                      {inv.customerAvatar}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{inv.customer}</p>
                      <p className="text-xs text-gray-400">{inv.customerEmail}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {inv.bookingId ? (
                    <span className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-600 font-medium">{inv.bookingId}</span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-4 text-gray-600 text-sm whitespace-nowrap">
                  {new Date(inv.issuedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`text-sm ${isOverdue(inv) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    {new Date(inv.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </td>
                <td className="px-4 py-4 text-right font-semibold text-gray-800 whitespace-nowrap">
                  {fmtAmount(inv.totalAmount)}
                </td>
                <td className="px-4 py-4 text-right whitespace-nowrap">
                  {inv.balanceDue > 0 ? (
                    <span className="font-semibold text-rose-600">{fmtAmount(inv.balanceDue)}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[inv.status]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[inv.status]}`} />
                    {inv.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-gray-500 text-sm whitespace-nowrap">{inv.agent}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); onSelect(inv); }}>
                      <i className="ri-eye-line text-sm" />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}>
                      <i className="ri-printer-line text-sm" />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}>
                      <i className="ri-more-2-line text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-bill-line text-3xl mb-2 block" />
            <p className="text-sm">No invoices found</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-50 flex items-center justify-between text-xs text-gray-400">
        <span>Showing {filtered.length} of {invoices.length} invoices</span>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">Previous</button>
          <button className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">Next</button>
        </div>
      </div>
    </div>
  );
}
